import { Card, Divider, Tag, Table, Button, Input, Form, Popconfirm, Avatar, Space } from 'antd';
import { DeleteOutlined, UserAddOutlined, UserOutlined, MailOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { retrieveAegisRecipients, addAegisRecipient, deleteAegisRecipient } from '../../methods/redux/reducers/compliance';

const Container = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	gap: 1em;
`;

const AegisRecipients = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const aegisRecipients = useAppSelector((store) => store.compliance.aegisRecipients) as any;
	const [loading, setLoading] = useState(false);
	const [form] = Form.useForm();

	const fetchData = async () => {
		setLoading(true);
		await dispatch(retrieveAegisRecipients());
		setLoading(false);
	};

	useEffect(() => {
		fetchData();
	}, []);

	const onFinish = async (values: { email: string }) => {
		const success = await addAegisRecipient(values.email);
		if (success) {
			form.resetFields();
			fetchData();
		}
	};

	const handleDelete = async (email: string) => {
		const success = await deleteAegisRecipient(email);
		if (success) {
			fetchData();
		}
	};

	const columns = [
		{
			title: 'Administrator',
			key: 'admin',
			render: (record: any) => (
				<Space>
					<Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
					<div>
						<div style={{ fontWeight: 'bold' }}>{record.fullName || 'No Name'}</div>
						<small style={{ color: '#8c8c8c' }}>@{record.username || 'n/a'}</small>
					</div>
				</Space>
			),
		},
		{
			title: 'Email Address',
			dataIndex: 'email',
			key: 'email',
			render: (email: string) => (
				<Space>
					<MailOutlined style={{ color: '#1890ff' }} />
					<span>{email}</span>
				</Space>
			),
		},
		{
			title: 'Action',
			key: 'action',
			render: (record: any) => (
				<Popconfirm
					title="Are you sure you want to remove this recipient?"
					onConfirm={() => handleDelete(record.email)}
					okText="Yes"
					cancelText="No"
				>
					<Button type="link" danger icon={<DeleteOutlined />}>
						Remove
					</Button>
				</Popconfirm>
			),
		},
	];

	// Handle the records array from the API response
	const dataSource = aegisRecipients?.records || [];

	return (
		<Container>
			<Card>
				<div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
					<Button 
						type="text" 
						icon={<ArrowLeftOutlined />} 
						onClick={() => navigate(-1)} 
						style={{ fontSize: '18px' }}
					/>
					<div>
						<h1>Aegis Report Recipients</h1>
						<p>Manage admin email addresses that receive automated security reports.</p>
					</div>
				</div>
				<Divider />

				<Form 
					form={form} 
					onFinish={onFinish} 
					layout="inline" 
					style={{ marginBottom: '2em' }}
				>
					<Form.Item
						name="email"
						rules={[
							{ required: true, message: 'Please input an email!' },
							{ type: 'email', message: 'Please enter a valid email!' }
						]}
						style={{ minWidth: '300px' }}
					>
						<Input prefix={<UserAddOutlined />} placeholder="Admin Email Address" />
					</Form.Item>
					<Form.Item>
						<Button type="primary" htmlType="submit">
							Add Recipient
						</Button>
					</Form.Item>
				</Form>

				<Table
					columns={columns}
					dataSource={dataSource}
					rowKey="email"
					loading={loading}
					pagination={{ pageSize: 10 }}
				/>
			</Card>
		</Container>
	);
};

export default AegisRecipients;
